import { css } from '@emotion/react';
import React, { memo, useMemo } from 'react';
import dayjs from 'dayjs';
import { fontWeight600 } from '~/css/font';
import { ComposedChart, ResponsiveContainer, CartesianGrid, Tooltip, XAxis, YAxis, Legend, ReferenceLine, Line, } from 'recharts-new';
import { flex } from '~/modules/AppLayout/FlexGridCss';
import { useTradingSummaryState } from '~/modules/tradeSheet/tradingSummary/useTradingSummaryState';
const renderLegend = (props) => {
    const { payload } = props;
    return (<ul css={flex.h.allCenter}>
      {payload.map((key, index) => {
            let title;
            if (key.dataKey === 'mine')
                title = '我的';
            else if (key.dataKey === 'txf')
                title = '大盤';
            else if (key.dataKey === 'adviser')
                title = '期顧';
            return (<li key={`item-${index}`} data-list-style-type={'circle'} css={css `
              font-size: 20px;
              ${fontWeight600};
              color: ${key.color};
            `}>
            <p css={css `
                width: 70px;
                font-size: 16px;
                ${fontWeight600};
                color: ${key.color};
              `}>
              {title}
            </p>
          </li>);
        })}
    </ul>);
};
const AccumulatedReturnRateChart = memo(function AccumulatedReturnRateChart(props) {
    const { state } = useTradingSummaryState.useContainer();
    const { rawDailyReturnRate } = state;
    const oldest = rawDailyReturnRate ? rawDailyReturnRate[0] : undefined;
    const data = useMemo(() => rawDailyReturnRate?.map(daily => ({
        mine: daily.customerReturnRate ?? 0,
        txf: daily.txf,
        adviser: daily.futuresAdviser,
        time: dayjs(daily.date).unix(),
        adjustTime: (dayjs(daily.date).unix() - dayjs(oldest?.date).unix()) / 1000,
    })) ?? [], [rawDailyReturnRate, oldest?.date]);
    const CustomizedXAxisLabel = memo(function CustomizedXAxisLabel({ tickProps }) {
        const { x, y, payload } = tickProps;
        const origin = data.find(datum => datum.adjustTime === payload.value);
        return (<svg css={css `
          cursor: pointer;
        `}>
        <text textAnchor='middle' x={x} y={y + 4} fontSize={16}>
          {origin && dayjs(origin.time * 1000).format('MM/DD')}
        </text>
      </svg>);
    });
    if (data.length === 0)
        return (<div css={css `
          height: 100%;
          ${flex.h.allCenter};
        `}>
        無此區間資料
      </div>);
    return (<div style={{ width: '100%', height: 300 }}>
      <ResponsiveContainer width='100%' height='100%'>
        <ComposedChart data={data}>
          <CartesianGrid stroke='#eee' strokeDasharray='5 5'/>
          <XAxis dataKey='adjustTime' type='number' ticks={[...data.map(d => d.adjustTime)]} tick={p => <CustomizedXAxisLabel tickProps={p}/>} interval={20} tickMargin={20}/>
          <YAxis tickLine={false} tick={p => <CustomizedYAxisLabel tickProps={p}/>} tickMargin={20}/>
          <ReferenceLine y={0} stroke='#aaaaaa' isFront={true}/>
          <Tooltip content={<CustomizedTooltip />}/>
          <Line type='basis' dataKey='mine' stroke={'#e53238'} dot={{ r: 0 }} strokeWidth={2} isAnimationActive={false}/>
          <Line type='basis' dataKey='txf' stroke={'#0064d2'} dot={{ r: 0 }} strokeWidth={2} isAnimationActive={false}/>
          <Line type='basis' dataKey='adviser' stroke={'#f5af02'} dot={{ r: 0 }} strokeWidth={2} isAnimationActive={false}/>
          <Legend content={renderLegend}/>
        </ComposedChart>
      </ResponsiveContainer>
    </div>);
});
const CustomizedTooltip = ({ payload }) => {
    // 只會顯示有合理化在圖上的欄位，所以拿tradeValueKey比對去拿Datum物件
    if (!payload || (payload && payload.length < 1))
        return null;
    const datum = payload[0].payload;
    return (<div css={css `
        background: #ffffff;
        padding: 0 8px;
        border-radius: 8px;
        border: #a0a0a0 1px solid;
      `}>
      <p>{dayjs(datum.time * 1000).format('MM/DD')}</p>
      <p css={css `
          color: #e53238;
          ${fontWeight600};
        `}>
        我的： {datum.mine}%
      </p>
      <p css={css `
          color: #0064d2;
          ${fontWeight600};
        `}>
        大盤： {datum.txf}%
      </p>
      <p css={css `
          color: #f5af02;
          ${fontWeight600};
        `}>
        期顧： {datum.adviser}%
      </p>
    </div>);
};
const CustomizedYAxisLabel = memo(function CustomizedYAxisLabel({ tickProps }) {
    const { x, y, payload } = tickProps;
    return (<svg css={css `
        cursor: pointer;
        font-size: 16px;
      `}>
      <text textAnchor='middle' x={x + 6} y={y + 6}>
        {payload?.value}%
      </text>
    </svg>);
});
export default AccumulatedReturnRateChart;
